import type { ReactNode } from 'react'

import { externalLinkProps } from '@/lib/externalLinks'

/**
 * The little bit of Markdown the assistant actually writes, as React elements.
 *
 * Its answers arrive as Markdown - bold figures, bullet lists, and tables when
 * it is listing records - and printing them raw put `**Approved Advisory
 * Rate:**` on screen in front of people.
 *
 * Two decisions worth stating.
 *
 * **No library.** This frontend has five runtime dependencies; react-markdown
 * and a GFM plugin would be two more, for one panel, and pull a parser and a
 * plugin pipeline in with them. What the assistant emits is a small, known
 * subset, and this is a hundred lines.
 *
 * **No HTML, ever.** The text comes from an outside service, so it is never
 * handed to `dangerouslySetInnerHTML`: every piece of it becomes a React text
 * node or an element this file created. Markup in an answer therefore renders
 * as the characters it is, and cannot execute. Links are built from the parsed
 * href and only when it is `http(s)`, so `javascript:` in an answer is drawn as
 * text rather than followed.
 *
 * Anything not recognised falls through as a paragraph of plain text, which is
 * exactly what it looked like before. Nothing is ever dropped.
 */

/** `**bold**`, `*italic*`, `` `code` `` and `[text](href)`, in one pass. */
const INLINE = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`\n]+`|\[[^\]\n]+\]\([^)\s]+\))/g

function inline(text: string, keyPrefix: string): ReactNode[] {
  return text.split(INLINE).map((piece, i) => {
    const key = `${keyPrefix}-${i}`
    if (!piece) return null

    if (piece.startsWith('**') && piece.endsWith('**') && piece.length > 4) {
      return <strong key={key}>{piece.slice(2, -2)}</strong>
    }
    if (piece.startsWith('`') && piece.endsWith('`') && piece.length > 2) {
      return <code key={key}>{piece.slice(1, -1)}</code>
    }
    if (piece.startsWith('*') && piece.endsWith('*') && piece.length > 2) {
      return <em key={key}>{piece.slice(1, -1)}</em>
    }
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(piece)
    if (link) {
      const [, label, href] = link
      // Only a real web address becomes a link. Anything else - a bare word, a
      // javascript: url - is shown as the text it is.
      if (/^https?:\/\//i.test(href)) {
        return (
          <a key={key} href={href} {...externalLinkProps(href)}>
            {label}
          </a>
        )
      }
      return <span key={key}>{label}</span>
    }
    return <span key={key}>{piece}</span>
  })
}

const BULLET = /^\s*[-*+]\s+(.*)$/
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/
const HEADING = /^\s*(#{1,6})\s+(.*)$/
const TABLE_ROW = /^\s*\|(.+)\|\s*$/
const TABLE_RULE = /^\s*\|[\s:|-]+\|\s*$/

function cells(row: string): string[] {
  return (TABLE_ROW.exec(row)?.[1] ?? '')
    .split('|')
    .map((c) => c.trim())
}

export function renderMarkdown(text: string): ReactNode {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const out: ReactNode[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (!line.trim()) {
      i += 1
      continue
    }

    // A table: a header row, its rule, and the rows under it.
    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_RULE.test(lines[i + 1])) {
      const head = cells(line)
      const body: string[][] = []
      i += 2
      while (i < lines.length && TABLE_ROW.test(lines[i])) {
        body.push(cells(lines[i]))
        i += 1
      }
      out.push(
        <table className="md-table" key={`t-${i}`}>
          <thead>
            <tr>
              {head.map((c, n) => (
                <th key={n}>{inline(c, `th-${i}-${n}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, r) => (
              <tr key={r}>
                {row.map((c, n) => (
                  <td key={n}>{inline(c, `td-${i}-${r}-${n}`)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      )
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      out.push(
        <p className="md-head" key={`h-${i}`}>
          {inline(heading[2], `h-${i}`)}
        </p>,
      )
      i += 1
      continue
    }

    // A run of bullets, or a run of numbered items.
    if (BULLET.test(line) || NUMBERED.test(line)) {
      const numbered = !BULLET.test(line)
      const items: string[] = []
      while (i < lines.length) {
        const match = numbered ? NUMBERED.exec(lines[i]) : BULLET.exec(lines[i])
        if (!match) break
        items.push(match[1])
        i += 1
      }
      const rows = items.map((item, n) => <li key={n}>{inline(item, `li-${i}-${n}`)}</li>)
      out.push(
        numbered ? (
          <ol className="md-list" key={`l-${i}`}>
            {rows}
          </ol>
        ) : (
          <ul className="md-list" key={`l-${i}`}>
            {rows}
          </ul>
        ),
      )
      continue
    }

    // Everything else is a paragraph: consecutive plain lines, joined.
    const paragraph: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !BULLET.test(lines[i]) &&
      !NUMBERED.test(lines[i]) &&
      !HEADING.test(lines[i]) &&
      !TABLE_ROW.test(lines[i])
    ) {
      paragraph.push(lines[i].trim())
      i += 1
    }
    if (paragraph.length) {
      out.push(
        <p className="md-p" key={`p-${i}`}>
          {inline(paragraph.join(' '), `p-${i}`)}
        </p>,
      )
    }
  }

  return out.length ? out : text
}
